import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { crmService, type Paginated } from '@/services/crm.service';
import type { AdminUser } from '@/services/admin.service';
import type {
  Contact,
  CrmNote,
  CrmReport,
  CrmSummary,
  OutreachLog,
  PipelineStage,
  Reminder,
} from '@/types/crm.types';
import type { RootState } from '@/store';

export interface CrmState {
  contacts: Contact[];
  contactsLoading: boolean;
  contactsError: string | null;
  contactsLoadedAt: number | null;

  admins: AdminUser[];
  adminsLoading: boolean;

  outreach: OutreachLog[];
  outreachLoading: boolean;

  reminders: Reminder[];
  remindersLoading: boolean;

  summary: CrmSummary | null;
  summaryLoading: boolean;

  report: CrmReport | null;
  reportScope: 'own' | 'team';
  reportLoading: boolean;

  // Per-contact detail cache
  activeContact: Contact | null;
  activeContactOutreach: OutreachLog[];
  activeContactNotes: CrmNote[];
  activeContactReminders: Reminder[];
  activeContactLoading: boolean;
}

const initialState: CrmState = {
  contacts: [],
  contactsLoading: false,
  contactsError: null,
  contactsLoadedAt: null,

  admins: [],
  adminsLoading: false,

  outreach: [],
  outreachLoading: false,

  reminders: [],
  remindersLoading: false,

  summary: null,
  summaryLoading: false,

  report: null,
  reportScope: 'own',
  reportLoading: false,

  activeContact: null,
  activeContactOutreach: [],
  activeContactNotes: [],
  activeContactReminders: [],
  activeContactLoading: false,
};

const STALE_MS = 2 * 60 * 1000; // 2 minutes

// ─── Thunks ────────────────────────────────────────────────

export const fetchContacts = createAsyncThunk(
  'crm/fetchContacts',
  async (force: boolean | undefined, { getState, rejectWithValue }) => {
    const state = (getState() as RootState).crm;
    if (
      !force &&
      state.contactsLoadedAt &&
      Date.now() - state.contactsLoadedAt < STALE_MS
    ) {
      return null; // still fresh
    }
    try {
      let all: Contact[] = [];
      let p = 1;
      let more = true;
      while (more && p <= 40) {
        const res: Paginated<Contact> = await crmService.listContacts({ page: p, limit: 100 });
        all = all.concat(res.items);
        more = res.pagination.page < res.pagination.totalPages;
        p++;
      }
      return all;
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load contacts');
    }
  }
);

export const fetchAdmins = createAsyncThunk(
  'crm/fetchAdmins',
  async (_, { getState, rejectWithValue }) => {
    const state = (getState() as RootState).crm;
    if (state.admins.length > 0) return null;
    try {
      return await crmService.listAssignableAdmins();
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load admins');
    }
  }
);

export const fetchOutreach = createAsyncThunk(
  'crm/fetchOutreach',
  async (_, { rejectWithValue }) => {
    try {
      return await crmService.listOutreach({ limit: 500 });
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load outreach');
    }
  }
);

export const fetchReminders = createAsyncThunk(
  'crm/fetchReminders',
  async (_, { rejectWithValue }) => {
    try {
      return await crmService.listReminders({ status: 'pending', limit: 500 });
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load reminders');
    }
  }
);

export const fetchSummary = createAsyncThunk(
  'crm/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      return await crmService.getSummary();
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load summary');
    }
  }
);

export const fetchReport = createAsyncThunk(
  'crm/fetchReport',
  async (scope: 'own' | 'team', { rejectWithValue }) => {
    try {
      return { report: await crmService.getReport({ scope }), scope };
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load report');
    }
  }
);

export const fetchContactDetail = createAsyncThunk(
  'crm/fetchContactDetail',
  async (contactId: string, { rejectWithValue }) => {
    try {
      const [contact, outreach, notes, reminders] = await Promise.all([
        crmService.getContact(contactId),
        crmService.listOutreach({ contactId, limit: 100 }).catch(() => [] as OutreachLog[]),
        crmService.listNotes({ contactId, limit: 100 }).catch(() => [] as CrmNote[]),
        crmService.listReminders({ contactId, limit: 100 }).catch(() => [] as Reminder[]),
      ]);
      return { contact, outreach, notes, reminders };
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load contact');
    }
  }
);

// ─── Mutations (optimistic + API) ──────────────────────────

export const assignContact = createAsyncThunk(
  'crm/assignContact',
  async (
    { contactId, adminId }: { contactId: string; adminId: string | null },
    { rejectWithValue }
  ) => {
    try {
      await crmService.assignContact(contactId, adminId);
      return { contactId, adminId };
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to assign');
    }
  }
);

export const moveContactStage = createAsyncThunk(
  'crm/moveContactStage',
  async (
    { contactId, stage }: { contactId: string; stage: PipelineStage },
    { rejectWithValue }
  ) => {
    try {
      await crmService.updateContact(contactId, { stage });
      return { contactId, stage };
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to move stage');
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────

const crmSlice = createSlice({
  name: 'crm',
  initialState,
  reducers: {
    invalidateContacts(state) {
      state.contactsLoadedAt = null;
    },
    updateContactLocally(
      state,
      action: PayloadAction<{ id: string; patch: Partial<Contact> }>
    ) {
      const { id, patch } = action.payload;
      state.contacts = state.contacts.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      );
      if (state.activeContact?.id === id) {
        state.activeContact = { ...state.activeContact, ...patch };
      }
    },
    addOutreachLocally(state, action: PayloadAction<OutreachLog>) {
      state.outreach.unshift(action.payload);
      state.activeContactOutreach.unshift(action.payload);
    },
    addNoteLocally(state, action: PayloadAction<CrmNote>) {
      state.activeContactNotes.unshift(action.payload);
    },
    removeNoteLocally(state, action: PayloadAction<string>) {
      state.activeContactNotes = state.activeContactNotes.filter(
        (n) => n.id !== action.payload
      );
    },
    removeOutreachLocally(state, action: PayloadAction<string>) {
      state.outreach = state.outreach.filter((o) => o.id !== action.payload);
      state.activeContactOutreach = state.activeContactOutreach.filter(
        (o) => o.id !== action.payload
      );
    },
    updateReminderLocally(
      state,
      action: PayloadAction<{ id: string; patch: Partial<Reminder> }>
    ) {
      const { id, patch } = action.payload;
      state.reminders = state.reminders.map((r) =>
        r.id === id ? { ...r, ...patch } : r
      );
      state.activeContactReminders = state.activeContactReminders.map((r) =>
        r.id === id ? { ...r, ...patch } : r
      );
    },
    addReminderLocally(state, action: PayloadAction<Reminder>) {
      state.reminders.unshift(action.payload);
      state.activeContactReminders.unshift(action.payload);
    },
    clearActiveContact(state) {
      state.activeContact = null;
      state.activeContactOutreach = [];
      state.activeContactNotes = [];
      state.activeContactReminders = [];
    },
  },
  extraReducers: (builder) => {
    // Contacts
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.contactsLoading = true;
        state.contactsError = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contactsLoading = false;
        if (action.payload !== null) {
          state.contacts = action.payload;
          state.contactsLoadedAt = Date.now();
        }
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.contactsLoading = false;
        state.contactsError = action.payload as string;
      });

    // Admins
    builder
      .addCase(fetchAdmins.pending, (state) => {
        state.adminsLoading = true;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.adminsLoading = false;
        if (action.payload !== null) state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state) => {
        state.adminsLoading = false;
      });

    // Outreach
    builder
      .addCase(fetchOutreach.pending, (state) => {
        state.outreachLoading = true;
      })
      .addCase(fetchOutreach.fulfilled, (state, action) => {
        state.outreachLoading = false;
        state.outreach = action.payload;
      })
      .addCase(fetchOutreach.rejected, (state) => {
        state.outreachLoading = false;
      });

    // Reminders
    builder
      .addCase(fetchReminders.pending, (state) => {
        state.remindersLoading = true;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.remindersLoading = false;
        state.reminders = action.payload;
      })
      .addCase(fetchReminders.rejected, (state) => {
        state.remindersLoading = false;
      });

    // Summary
    builder
      .addCase(fetchSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchSummary.rejected, (state) => {
        state.summaryLoading = false;
      });

    // Report
    builder
      .addCase(fetchReport.pending, (state) => {
        state.reportLoading = true;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.report = action.payload.report;
        state.reportScope = action.payload.scope;
      })
      .addCase(fetchReport.rejected, (state) => {
        state.reportLoading = false;
      });

    // Contact detail
    builder
      .addCase(fetchContactDetail.pending, (state) => {
        state.activeContactLoading = true;
      })
      .addCase(fetchContactDetail.fulfilled, (state, action) => {
        state.activeContactLoading = false;
        state.activeContact = action.payload.contact;
        state.activeContactOutreach = action.payload.outreach;
        state.activeContactNotes = action.payload.notes;
        state.activeContactReminders = action.payload.reminders;
      })
      .addCase(fetchContactDetail.rejected, (state) => {
        state.activeContactLoading = false;
      });

    // Assign
    builder.addCase(assignContact.fulfilled, (state, action) => {
      const { contactId, adminId } = action.payload;
      const adminName = state.admins.find((a) => a.userId === adminId)?.name || null;
      state.contacts = state.contacts.map((c) =>
        c.id === contactId
          ? { ...c, assignedTo: adminId, assignedToName: adminName }
          : c
      );
      if (state.activeContact?.id === contactId) {
        state.activeContact = {
          ...state.activeContact,
          assignedTo: adminId,
          assignedToName: adminName,
        };
      }
    });

    // Move stage
    builder.addCase(moveContactStage.fulfilled, (state, action) => {
      const { contactId, stage } = action.payload;
      state.contacts = state.contacts.map((c) =>
        c.id === contactId ? { ...c, stage } : c
      );
      if (state.activeContact?.id === contactId) {
        state.activeContact = { ...state.activeContact, stage };
      }
    });
  },
});

export const {
  invalidateContacts,
  updateContactLocally,
  addOutreachLocally,
  addNoteLocally,
  removeNoteLocally,
  removeOutreachLocally,
  updateReminderLocally,
  addReminderLocally,
  clearActiveContact,
} = crmSlice.actions;

export default crmSlice.reducer;
